import { watch } from 'vue'
import useAuthStore, { type TokenList } from '@/stores/authStore'

// PERMANENT composable (not legacy). ADA-4218/ADA-4410: customer tokens live in
// sessionStorage (they die when the browser closes), but a session must stay in
// sync across already-open tabs. sessionStorage never fires cross-tab `storage`
// events, so we restore the old PersistedStateClient broadcast behavior with a
// BroadcastChannel. ada-app-ui does NOT need this — it uses localStorage and
// syncs across tabs via storage events for free.
//
// The setup is module-level and memoized: the router's auth guard may run
// before App.vue's setup (vue-router starts the initial navigation on install),
// and it needs the channel alive to receive the session from a peer tab.
// Whichever of useAuthTabSync()/waitForPeerSessionHydration() runs first
// initializes; watchers outside a component are never auto-stopped, which is
// exactly right for app-lifetime sync.

interface SessionSnapshot {
  tokens: TokenList
  selectedToken: number
}

type AuthMessage = ({ type: 'sync' } & SessionSnapshot) | { type: 'logout' } | { type: 'request-session' }

const CHANNEL_NAME = 'mitt-amili-auth'
// BroadcastChannel latency is ~1-5 ms; 250 ms is a generous ceiling only ever
// paid (once) by a genuinely logged-out user opening the app.
const HYDRATION_TIMEOUT_MS = 250

/**
 * Settles once the startup handshake is done: `true` as soon as a peer tab's
 * session has been applied, `false` when no peer answered within the timeout.
 */
let hydration: Promise<boolean> | null = null

function init(): Promise<boolean> {
  if (hydration != null) return hydration

  const authStore = useAuthStore()
  const channel = new BroadcastChannel(CHANNEL_NAME)

  // Echo guard: a snapshot we just applied from a peer must not be rebroadcast.
  // We compare serialized state (robust against Vue's async watcher flush) rather
  // than a time-based flag.
  let lastSignal = ''

  function snapshot(): SessionSnapshot {
    return {
      tokens: authStore.tokensList.map(({ token }) => ({ token })),
      selectedToken: authStore.tokensList.findIndex((t) => t.selected),
    }
  }

  function applyRemote(msg: SessionSnapshot) {
    // Set lastSignal synchronously BEFORE mutating: the watcher flushes
    // afterwards, reads the final snapshot and sees sig === lastSignal → no
    // rebroadcast.
    lastSignal = JSON.stringify({ tokens: msg.tokens, selectedToken: msg.selectedToken })
    authStore.setTokenList(msg.tokens, { storage: 'session' })
    // Out-of-range selectedToken (shouldn't happen) → sync converges via one extra sync.
    const entry = authStore.tokensList[msg.selectedToken]
    if (entry != null) authStore.selectToken(entry.token)
  }

  // Local auth changes → broadcast to peers.
  watch(
    () => JSON.stringify(snapshot()),
    (sig) => {
      if (sig === lastSignal) return // came from a peer — don't echo back
      const state = snapshot()
      channel.postMessage(state.tokens.length === 0 ? { type: 'logout' } : { type: 'sync', ...state })
    }
  )

  channel.onmessage = (event: MessageEvent<AuthMessage>) => {
    const msg = event.data
    if (msg.type === 'sync') {
      applyRemote(msg)
    } else if (msg.type === 'logout') {
      lastSignal = JSON.stringify({ tokens: [], selectedToken: -1 })
      authStore.clearAllTokens()
    } else {
      // 'request-session': a peer started without a session and is asking —
      // reply if we have one.
      if (authStore.tokensList.length > 0) {
        channel.postMessage({ type: 'sync', ...snapshot() })
      }
    }
  }

  const isAuthenticated = () => authStore.tokensList.length > 0

  if (isAuthenticated()) {
    hydration = Promise.resolve(true)
  } else {
    // Hydration on startup: ask already-open tabs for their session, and let
    // callers (the router auth guard) hold their decision until a peer answers
    // or the timeout passes.
    channel.postMessage({ type: 'request-session' })
    hydration = new Promise<boolean>((resolve) => {
      const stop = watch(isAuthenticated, (authed) => {
        if (authed) {
          stop()
          resolve(true)
        }
      })
      setTimeout(() => {
        stop()
        resolve(false)
      }, HYDRATION_TIMEOUT_MS)
    })
  }

  return hydration
}

export function useAuthTabSync() {
  // Older Safari (<15.4) lacks BroadcastChannel — degrade silently to a per-tab
  // session (still sessionStorage, just no cross-tab sync) rather than crash.
  // Same graceful-degradation pattern useAuth.refreshToken uses for navigator.locks.
  if (typeof BroadcastChannel === 'undefined') return
  void init()
}

/**
 * Router auth guard helper: a freshly opened tab may be about to receive the
 * session from an open peer tab. Await this before deciding to redirect to
 * /authenticate so deep links opened in a new tab aren't bounced to the start
 * page. Memoized — only the first navigation ever waits.
 */
export async function waitForPeerSessionHydration(): Promise<boolean> {
  if (typeof BroadcastChannel === 'undefined') return false
  return init()
}
